import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Calculator, Cloud, Trophy, Plus, Sparkles, X } from 'lucide-react';

interface InteractiveElement {
  type: 'poll' | 'calculator' | 'wordcloud' | 'quiz';
  title: string;
  config: any;
}

interface InteractiveElementSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (element: InteractiveElement) => void;
}

const elementTypes = [
  {
    type: 'poll' as const,
    icon: BarChart3,
    name: 'Live Poll',
    description: 'Create polls that show real-time results from all course participants',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    type: 'calculator' as const,
    icon: Calculator,
    name: 'Interactive Calculator',
    description: 'Add calculators for ROI, budgets, or custom formulas',
    color: 'from-green-500 to-emerald-500'
  },
  {
    type: 'wordcloud' as const,
    icon: Cloud,
    name: 'Word Cloud',
    description: 'Collect responses that generate a visual word cloud',
    color: 'from-purple-500 to-pink-500'
  },
  {
    type: 'quiz' as const,
    icon: Trophy,
    name: 'Quiz with Leaderboard',
    description: 'Create engaging quizzes with competitive leaderboards',
    color: 'from-orange-500 to-red-500'
  }
];

export const InteractiveElementSelector: React.FC<InteractiveElementSelectorProps> = ({
  open,
  onClose,
  onSelect
}) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [config, setConfig] = useState({
    title: '',
    question: '',
    options: ['', ''],
    formula: '',
    inputs: [{ label: '', id: '' }],
    calculatorType: 'roi',
    questions: [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]
  });

  const calculatorTypes = {
    roi: {
      name: 'ROI Calculator',
      inputs: [
        { label: 'Initial Investment', id: 'investment' },
        { label: 'Revenue Generated', id: 'revenue' }
      ],
      formula: 'return ((revenue - investment) / investment * 100).toFixed(2) + "%"',
      resultLabel: 'Return on Investment'
    },
    savings: {
      name: 'Savings Calculator',
      inputs: [
        { label: 'Current Cost', id: 'currentCost' },
        { label: 'New Cost', id: 'newCost' }
      ],
      formula: 'return (currentCost - newCost).toFixed(2)',
      resultLabel: 'Total Savings'
    },
    budget: {
      name: 'Budget Calculator',
      inputs: [
        { label: 'Total Budget', id: 'budget' },
        { label: 'Amount Spent', id: 'spent' }
      ],
      formula: 'return (budget - spent).toFixed(2)',
      resultLabel: 'Remaining Budget'
    },
    percentage: {
      name: 'Percentage Calculator',
      inputs: [
        { label: 'Total Value', id: 'total' },
        { label: 'Partial Value', id: 'partial' }
      ],
      formula: 'return ((partial / total) * 100).toFixed(2) + "%"',
      resultLabel: 'Percentage'
    }
  };

  const handleCreate = () => {
    if (!selectedType) return;

    let elementConfig = {};
    
    switch (selectedType) {
      case 'poll':
        elementConfig = {
          question: config.question,
          options: config.options.filter(o => o.trim())
        };
        break;
      case 'calculator':
        const calcType = calculatorTypes[config.calculatorType as keyof typeof calculatorTypes];
        elementConfig = {
          title: config.title || calcType.name,
          inputs: calcType.inputs,
          formula: calcType.formula,
          resultLabel: calcType.resultLabel
        };
        break;
      case 'wordcloud':
        elementConfig = {
          prompt: config.question
        };
        break;
      case 'quiz':
        elementConfig = {
          questions: config.questions.filter(q => q.question.trim() && q.options.some(o => o.trim()))
        };
        break;
    }

    onSelect({
      type: selectedType as any,
      title: config.title || config.question,
      config: elementConfig
    });

    // Reset
    setSelectedType(null);
    setConfig({
      title: '',
      question: '',
      options: ['', ''],
      formula: '',
      inputs: [{ label: '', id: '' }],
      calculatorType: 'roi',
      questions: [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]
    });
    onClose();
  };

  if (!selectedType) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Add Interactive Element
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            {elementTypes.map((element) => {
              const Icon = element.icon;
              return (
                <Card
                  key={element.type}
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  onClick={() => setSelectedType(element.type)}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${element.color} flex items-center justify-center mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{element.name}</h3>
                    <p className="text-sm text-muted-foreground">{element.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure {elementTypes.find(e => e.type === selectedType)?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {selectedType === 'poll' && (
            <>
              <div>
                <Label>Poll Question</Label>
                <Input
                  value={config.question}
                  onChange={(e) => setConfig({ ...config, question: e.target.value })}
                  placeholder="What's your biggest challenge?"
                />
              </div>
              <div>
                <Label>Options</Label>
                {config.options.map((option, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...config.options];
                        newOptions[index] = e.target.value;
                        setConfig({ ...config, options: newOptions });
                      }}
                      placeholder={`Option ${index + 1}`}
                    />
                    {config.options.length > 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newOptions = config.options.filter((_, i) => i !== index);
                          setConfig({ ...config, options: newOptions });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfig({ ...config, options: [...config.options, ''] })}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </div>
            </>
          )}

          {selectedType === 'calculator' && (
            <>
              <div>
                <Label>Calculator Type</Label>
                <Select
                  value={config.calculatorType}
                  onValueChange={(value) => setConfig({ ...config, calculatorType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(calculatorTypes).map(([key, calc]) => (
                      <SelectItem key={key} value={key}>{calc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Custom Title (Optional)</Label>
                <Input
                  value={config.title}
                  onChange={(e) => setConfig({ ...config, title: e.target.value })}
                  placeholder={calculatorTypes[config.calculatorType as keyof typeof calculatorTypes].name}
                />
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2">Preview Inputs:</h4>
                {calculatorTypes[config.calculatorType as keyof typeof calculatorTypes].inputs.map((input, i) => (
                  <div key={i} className="text-sm text-muted-foreground">• {input.label}</div>
                ))}
              </div>
            </>
          )}

          {selectedType === 'wordcloud' && (
            <div>
              <Label>Prompt</Label>
              <Textarea
                value={config.question}
                onChange={(e) => setConfig({ ...config, question: e.target.value })}
                placeholder="In one sentence, what is your key takeaway?"
                rows={3}
              />
            </div>
          )}

          {selectedType === 'quiz' && (
            <>
              <div className="space-y-4">
                <Label>Quiz Questions</Label>
                {config.questions.map((q, qIndex) => (
                  <Card key={qIndex} className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-medium">Question {qIndex + 1}</h4>
                      {config.questions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newQuestions = config.questions.filter((_, i) => i !== qIndex);
                            setConfig({ ...config, questions: newQuestions });
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <Input
                        value={q.question}
                        onChange={(e) => {
                          const newQuestions = [...config.questions];
                          newQuestions[qIndex].question = e.target.value;
                          setConfig({ ...config, questions: newQuestions });
                        }}
                        placeholder="Enter your question"
                      />
                      
                      <div>
                        <Label className="text-xs">Answer Options</Label>
                        {q.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex gap-2 mb-2 items-center">
                            <input
                              type="radio"
                              name={`correct-${qIndex}`}
                              checked={q.correctAnswer === optIndex}
                              onChange={() => {
                                const newQuestions = [...config.questions];
                                newQuestions[qIndex].correctAnswer = optIndex;
                                setConfig({ ...config, questions: newQuestions });
                              }}
                              className="w-4 h-4"
                            />
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newQuestions = [...config.questions];
                                newQuestions[qIndex].options[optIndex] = e.target.value;
                                setConfig({ ...config, questions: newQuestions });
                              }}
                              placeholder={`Answer ${optIndex + 1}`}
                            />
                            {q.options.length > 2 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newQuestions = [...config.questions];
                                  newQuestions[qIndex].options = q.options.filter((_, i) => i !== optIndex);
                                  if (q.correctAnswer >= optIndex && q.correctAnswer > 0) {
                                    newQuestions[qIndex].correctAnswer--;
                                  }
                                  setConfig({ ...config, questions: newQuestions });
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newQuestions = [...config.questions];
                            newQuestions[qIndex].options.push('');
                            setConfig({ ...config, questions: newQuestions });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Answer
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setConfig({
                    ...config,
                    questions: [...config.questions, { question: '', options: ['', '', '', ''], correctAnswer: 0 }]
                  })}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Question
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setSelectedType(null)}>
            Back
          </Button>
          <Button onClick={handleCreate}>
            Create Element
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
